import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Description, ErrorMessage, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Radio, RadioField, RadioGroup } from "src/components/base/radio";
import { Text } from "src/components/base/text";
import { UUID } from "src/api/api_common";
import { FullSubnetRouter } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link EnableSubnetDialog}
 */
export type EnableSubnetDialogProps = {
    /** Callback for closing the popup */
    onClose: () => void;

    /** Callback for successful enable */
    onEnable: () => void;

    /** The subnet */
    subnet: {
        /** Name of the subnet */
        name: string;
        /** UUID of the subnet */
        uuid: UUID;
    };

    /** Optional device that should route this subnet */
    router?: UUID;
};

/**
 * Dialog to disable a given subnet
 */
export default function EnableSubnetDialog(props: EnableSubnetDialogProps) {
    const [t] = useTranslation("dialog-enable-subnet");
    const [tg] = useTranslation();

    const [possibleRouters, setPossibleRouters] = React.useState<Array<FullSubnetRouter>>();

    const form = useForm({
        defaultValues: {
            router: null as UUID | null,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.router === null) {
                    return {
                        fields: {
                            router: t("error.selection-necessary"),
                        },
                    };
                }

                await enableSubnet(value.router);
            },
        },
    });

    /**
     * Enables an existing subnet
     *
     * @param router The device that should be set as router.
     */
    const enableSubnet = async (router?: UUID) => {
        const res = await ApiInternal.subnets.enable(props.subnet.uuid, { router: router });
        if (res.result === "Err") {
            if (res.error.routers !== undefined && res.error.routers !== null) {
                setPossibleRouters(res.error.routers);
            }
            return;
        }

        props.onEnable();
    };

    return (
        <>
            <Dialog open={true} onClose={props.onClose}>
                <DialogTitle>{t("heading.enable-subnet", { subnet: props.subnet.name })}</DialogTitle>
                <DialogDescription>{t("description.enable-subnet")}</DialogDescription>
                {possibleRouters?.length === 0 && (
                    <DialogBody>
                        <ErrorMessage>{t("error.missing-routers")}</ErrorMessage>
                    </DialogBody>
                )}
                <DialogActions>
                    <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                    <PrimaryButton onClick={async () => await enableSubnet(props.router)}>
                        {t("button.enable-subnet")}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            {/* possibleRouters === [] is a special error case handled in the original dialog */}
            {possibleRouters !== undefined && possibleRouters.length > 0 && (
                <Dialog open={true} onClose={props.onClose}>
                    <DialogTitle>{t("heading.select-router-device")}</DialogTitle>
                    <Form onSubmit={form.handleSubmit}>
                        <DialogBody>
                            <Fieldset>
                                <FieldGroup>
                                    <Text>{t("description.select-router-device")}</Text>

                                    <form.Field name={"router"}>
                                        {(fieldApi) => (
                                            <RadioGroup
                                                value={fieldApi.state.value ?? undefined}
                                                onChange={fieldApi.handleChange}
                                            >
                                                {possibleRouters.map((device) => (
                                                    <RadioField key={device.uuid}>
                                                        <Radio color={"blue"} value={device.uuid} />
                                                        <Label>{device.dnsname}</Label>
                                                        {device.tags.length > 0 && (
                                                            <Description>
                                                                <div className={"flex gap-1"}>
                                                                    {device.tags.map((tag) => (
                                                                        <TagBadge key={tag.uuid} tag={tag} />
                                                                    ))}
                                                                </div>
                                                            </Description>
                                                        )}
                                                    </RadioField>
                                                ))}
                                            </RadioGroup>
                                        )}
                                    </form.Field>
                                </FieldGroup>
                            </Fieldset>
                        </DialogBody>
                        <DialogActions>
                            <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                            <PrimaryButton type={"submit"}>{t("button.enable-subnet")}</PrimaryButton>
                        </DialogActions>
                    </Form>
                </Dialog>
            )}
        </>
    );
}
